/**
 * Demographics Layer Helpers
 *
 * Utilities for creating Mapbox GL JS expressions for demographics vector tiles
 */

import type { DynamicLayerConfig } from '../config/dynamicLayersConfig'

/**
 * Field mapping from layer ID to Elasticsearch aggregation field name
 */
const FIELD_MAP: Record<string, string> = {
  'demographics-population-density': 'population_density',
  'demographics-foreign-pct': 'foreign_pct_avg',
  'demographics-young-adult-pct': 'young_adult_pct_avg',
  'demographics-elderly-pct': 'elderly_pct_avg',
  'demographics-growth-rate': 'growth_rate_avg',
  'demographics-age-group': 'dominant_age_group_mode',
  'demographics-household-type': 'dominant_household_type_mode',
  'demographics-residence-duration': 'dominant_residence_duration_mode',
}

/**
 * Value ranges for continuous demographic layers
 */
const VALUE_RANGES: Record<string, number[]> = {
  'demographics-population-density': [0, 50, 100, 200, 300, 400],
  'demographics-foreign-pct': [0, 15, 30, 45, 60],
  'demographics-young-adult-pct': [0, 20, 30, 40, 50],
  'demographics-elderly-pct': [0, 15, 20, 25, 30],
  'demographics-growth-rate': [-5, -2, 0, 2, 5],
}

/**
 * Check if a layer is categorical (discrete values)
 */
export function isCategoricalLayer(layerId: string): boolean {
  return (
    layerId.includes('age-group') ||
    layerId.includes('household-type') ||
    layerId.includes('residence-duration')
  )
}

/**
 * Check if a layer is demographics-related
 */
export function isDemographicsLayer(layerId: string): boolean {
  return layerId.startsWith('demographics-')
}

/**
 * Get the ES field name for a demographics layer
 */
export function getDemographicsField(layerId: string): string | undefined {
  return FIELD_MAP[layerId]
}

/**
 * Create Mapbox fill-color expression for a demographics layer
 *
 * Returns a Mapbox expression that maps data values to colors
 */
export function createDemographicsFillColorExpression(
  layerId: string,
  layerConfig: DynamicLayerConfig
): any {
  const colors = layerConfig.render?.colors || []
  const field = getDemographicsField(layerId)

  if (!field) {
    console.warn(`No field mapping found for layer: ${layerId}`)
    return colors[0] || '#CCCCCC'
  }

  // For categorical layers, use match expression
  if (isCategoricalLayer(layerId)) {
    return createCategoricalExpression(field, colors)
  }

  // For continuous layers, use interpolate expression
  return createContinuousExpression(layerId, field, colors)
}

/**
 * Create a categorical (match) expression for discrete values
 */
function createCategoricalExpression(field: string, colors: string[]): any {
  const matchPairs: any[] = []

  // Build match pairs: value1, color1, value2, color2, ...
  for (let i = 0; i < colors.length; i++) {
    matchPairs.push(i + 1, colors[i])
  }

  return [
    'match',
    ['get', field],
    ...matchPairs,
    colors[0], // fallback color
  ]
}

/**
 * Create a continuous (interpolate) expression for numeric values
 */
function createContinuousExpression(
  layerId: string,
  field: string,
  colors: string[]
): any {
  const values = VALUE_RANGES[layerId]

  if (!values) {
    console.warn(`No value range defined for layer: ${layerId}`)
    // Return simple color if no range defined
    return colors[0] || '#CCCCCC'
  }

  // Build interpolation stops: value1, color1, value2, color2, ...
  const stops: any[] = []
  for (let i = 0; i < Math.min(values.length, colors.length); i++) {
    stops.push(values[i], colors[i])
  }

  return ['interpolate', ['linear'], ['get', field], ...stops]
}

/**
 * Get all demographics layer IDs
 */
export function getAllDemographicsLayerIds(): string[] {
  return Object.keys(FIELD_MAP)
}

/**
 * Demographics layer Z-ordering (bottom to top)
 * Later items in array render on top
 */
export const DEMOGRAPHICS_LAYER_ORDER = [
  'demographics-residence-duration', // Bottom
  'demographics-household-type',
  'demographics-age-group',
  'demographics-growth-rate',
  'demographics-elderly-pct',
  'demographics-young-adult-pct',
  'demographics-foreign-pct',
  'demographics-population-density', // Top (default visible)
]

/**
 * Get API URL for demographics vector tiles
 */
export function getDemographicsVectorTileUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl}/demographics/vector-tiles/{z}/{x}/{y}`
}

/**
 * Demographics vector tile source configuration
 */
export function createDemographicsVectorTileSource(apiBaseUrl: string) {
  return {
    type: 'vector' as const,
    tiles: [getDemographicsVectorTileUrl(apiBaseUrl)],
    minzoom: 10,
    maxzoom: 16,
  }
}

/**
 * Category labels for categorical layers
 */
const CATEGORY_LABELS: Record<string, Record<number, string>> = {
  'demographics-age-group': {
    1: '0-19 (Children & Teens)',
    2: '20-39 (Young Adults)',
    3: '40-64 (Middle Age)',
    4: '65+ (Seniors)',
  },
  'demographics-household-type': {
    1: 'Single Person',
    2: 'Couples',
    3: 'Families with Children',
    4: 'Single Parents',
    5: 'Other',
  },
  'demographics-residence-duration': {
    1: '<2 years (High Turnover)',
    2: '2-5 years (Moderate)',
    3: '5-10 years (Stable)',
    4: '10+ years (Very Stable)',
  },
}

/**
 * Format a demographics value for display in a tooltip
 */
export function formatDemographicsValue(
  layerId: string,
  value: number | string | undefined
): string {
  if (value === undefined || value === null) {
    return 'No data'
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(numValue)) {
    return 'No data'
  }

  // Categorical layers - return label
  if (isCategoricalLayer(layerId)) {
    const labels = CATEGORY_LABELS[layerId]
    return labels?.[numValue] || `Category ${numValue}`
  }

  // Continuous layers - format based on type
  if (layerId === 'demographics-population-density') {
    return `${Math.round(numValue)} people/km²`
  }

  if (layerId === 'demographics-growth-rate') {
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(1)}%`
  }

  // Percentage layers
  if (layerId.endsWith('-pct')) {
    return `${numValue.toFixed(1)}%`
  }

  return numValue.toFixed(1)
}

/**
 * Get the display name for a demographics layer (for tooltip title)
 */
export function getDemographicsLayerDisplayName(layerId: string): string {
  const names: Record<string, string> = {
    'demographics-population-density': 'Population Density',
    'demographics-foreign-pct': 'Foreign Population',
    'demographics-young-adult-pct': 'Young Adults (20-39)',
    'demographics-elderly-pct': 'Elderly (65+)',
    'demographics-growth-rate': 'Population Growth (5yr)',
    'demographics-age-group': 'Dominant Age Group',
    'demographics-household-type': 'Dominant Household Type',
    'demographics-residence-duration': 'Residence Duration',
  }
  return names[layerId] || layerId
}
